<template>
  <div class="tla-form-group">
    <label class="tla-form-group__label" v-if="!hideLabel">{{label}}</label>

    <div class="tla-datepicker" :class="{ 'tla-datepicker--active': isCalendarOpen }">
      <div
        class="tla-form-group__input tla-form-group__input--arrow"
        :class="{'tla-form-group__input--disabled' : isDisabled}"
        @click="openDatePicker(!isCalendarOpen)">
        <span>{{ inputPlaceholder }}</span>
      </div>
      <v-calendar
        v-show="isCalendarOpen"
        v-model="selectedDate"
        :min-date="minDate"
        :max-date="maxDate"
        :disabled-dates="disabledDatesFormatted"
        :attributes="attributes"
        :masks="{ weekdays: 'WW' }"
        @dayclick="onDateChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Watch
} from 'vue-property-decorator';

import VCalendar from 'v-calendar';
import DateUtils from '@/utils/DateUtils';
import IDatePickerDate from '../../models/date-picker/interfaces/IDatePickerDate';
import IDatePickerEvent from '../../models/date-picker/interfaces/IDatePickerEvent';

Vue.use(VCalendar);

@Component
export default class DatePickerComponent extends Vue {
  @Prop()
  public label!: string;

  @Prop()
  public placeholder!: string;

  @Prop()
  public amountDaysLimit!: number;

  @Prop({ default: 0 })
  public daysToSkip!: number;

  @Prop({ default: () => [] })
  public disabledDates!: Date[];

  @Prop({ default: true })
  public showOnlyFutureDates!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: false })
  public defaultCalendarOpen!: boolean;

  @Prop({ default: false })
  public hideLabel!: boolean;

  public isCalendarOpen: boolean = false;

  public selectedDate: Date | null = null;

  public get currentStep(): number {
    return this.$store.getters['enquiryModule/currentStep'];
  }

  public get minDate(): Date | null {
    if (!this.showOnlyFutureDates) {
      return null;
    }

    return DateUtils.addDays(this.daysToSkip);
  }

  public get maxDate(): Date | null {
    if (!this.amountDaysLimit) {
      return null;
    }

    return DateUtils.addDays(this.amountDaysLimit);
  }

  public get attributes() {
    return [
      {
        key: 'selected',
        highlight: {
          contentClass: 'tla-datepicker__day--selected'
        },
        dates: this.selectedDate
      }
    ];
  }

  public get disabledDatesFormatted(): IDatePickerDate[] {
    if (this.disabledDates) {
      return this.disabledDates.map((d) => ({ start: d, end: d }));
    }

    return [];
  }

  public get inputPlaceholder(): string {
    if (!this.selectedDate) {
      return this.placeholder;
    }

    return DateUtils.getDateFormatted(this.selectedDate, false, true);
  }

  public openDatePicker(newState: boolean) {
    if (newState && !this.isDisabled) {
      this.isCalendarOpen = true;
      return;
    }

    this.isCalendarOpen = false;
  }

  public onDateChange(event: IDatePickerEvent) {
    const isActive = event.isDisabled === false;

    if (isActive) {
      this.selectedDate = event.date;
    }
  }

  @Watch('selectedDate')
  public onSelectedDateChange(value: Date) {
    this.$emit('updateDate', value);
    this.openDatePicker(false);
  }

  @Watch('defaultCalendarOpen')
  public onDefaultCalendarOpenChange(value: boolean) {
    this.openDatePicker(value);
  }

  @Watch('currentStep')
  public onCurrentStepChange() {
    if (this.defaultCalendarOpen) {
      this.openDatePicker(true);
    }
  }

  public created() {
    this.openDatePicker(this.defaultCalendarOpen);
  }
}
</script>
